/**
 * Sign in (docs/AUTH.md). Real mode: "Continue with Google" (when Firebase is configured) or "Email me a code", then
 * the 6 digits. Dev mode and the demo: the mark, one line, and "pick who you are" (fake login, docs/ARCHITECTURE.md G7),
 * with a small "Try email sign-in" link to the real flow. The list comes from GET /demo/people, or the built-in demo
 * list in mock mode. If the API isn't in dev mode, we say so plainly (UX_SCOPE: blocked state).
 *
 * Starting (docs/ux/LAUNCH-PROPOSALS.md Wave 1 #1): `?signup=1` (the landing page's button) or a stored referral titles
 * the screen "Start your 25 days free", with "Sam invited you" when we know who, and the email code first.
 * "Continue with Google" shows only when the server says Google works (GET /auth/config, GOOGLE_SIGNIN=on).
 *
 * A site buy button (lib/buyIntent.ts) also starts here: the same title, one line on what comes next, and Google first (one tap).
 * A sign-up from the sample café with something tried (lib/demoCarry.ts) says what was kept; with nothing tried it says nothing
 * about progress. Both intents wait in storage, so Google's window or the email code never loses them.
 */
import { usePageMeasure } from "../lib/siteAnalytics.ts";
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { TRIAL_DAYS } from "@logwave/core";
import { createHttpApi, ApiError, type DemoPerson } from "../api.ts";
import { useAuth } from "../auth.tsx";
import { CleaverMark } from "../components/Brand.tsx";
import { Icon } from "../components/Icons.tsx";
import { DigitBoxes } from "../components/DigitBoxes.tsx";
import { Blocked, Button, Field, Notice, Skeleton } from "../components/ui.tsx";
import { AUTH_MESSAGES, authMessage, isEmail, type SessionGrant } from "../lib/authClient.ts";
import { GoogleCancelled, googleConfigured, googleIdToken } from "../lib/google.ts";
import { config, IS_MOCK } from "../env.ts";
import { ROLE_LABEL } from "../lib/labels.ts";
import { DEMO_PEOPLE } from "../mock/demo.ts";
import { DEMO_NV_PEOPLE } from "../mock/demo-nv.ts";
import { MOCK_DEMO_KEY } from "../session.tsx";
import { clearAppEmail, storedAppEmail } from "../lib/appLead.ts";
import { storedRef } from "../lib/referrals.ts";
import { TOUR_PERSON, TOUR_WORDS, freeTrialUrl, startTour } from "../lib/demoTour.ts";
import { pendingBuy, pickedLine } from "../lib/buyIntent.ts";
import { carryLine, demoCarry } from "../lib/demoCarry.ts";
import { pendingRead } from "../lib/readIntent.ts";
import { readWords } from "@logwave/core";

type Load = { kind: "loading" } | { kind: "ok"; people: DemoPerson[] } | { kind: "blocked"; why: string };

export function SignIn() {
  const auth = useAuth();
  // Views, scroll and click positions only (lib/siteAnalytics.ts); never what is typed. A sign-up that came from a guide or an article
  // ("You were reading", lib/readIntent.ts) is its own page name, so the report shows whether naming it moves the taps on.
  const [readFrom] = useState(() => pendingRead());
  usePageMeasure(readFrom ? "app:sign-in/read" : "app:sign-in");
  // A session that just expired goes straight to the email sign-in, with the message.
  const [email, setEmail] = useState(!!auth.notice);
  if (auth.mode === "real") return <EmailSignIn />;
  if (email) return <EmailSignIn onBack={() => setEmail(false)} />;
  return <PickPerson onEmail={() => setEmail(true)} />;
}

function PickPerson({ onEmail }: { onEmail: () => void }) {
  const auth = useAuth();
  const [load, setLoad] = useState<Load>(IS_MOCK ? { kind: "ok", people: [...DEMO_PEOPLE, ...DEMO_NV_PEOPLE] } : { kind: "loading" });
  const [other, setOther] = useState(false);
  const [email, setEmail] = useState("");
  const [fresh, setFresh] = useState(false);
  const [err, setErr] = useState<string>();

  useEffect(() => {
    if (config.apiMode === "mock") return;
    let alive = true;
    createHttpApi({ baseUrl: config.apiUrl, auth: async () => ({}) }).demoPeople()
      .then((people) => { if (alive) setLoad({ kind: "ok", people }); })
      .catch((e: unknown) => {
        if (!alive) return;
        const why = e instanceof ApiError && (e.status === 404 || e.status === 403)
          ? "This server isn't in demo mode, so there's no one to pick. Start the API with AUTH_MODE=dev on this computer to use the demo sign-in."
          : `We couldn't reach CleanCleaver at ${config.apiUrl}. Is the API running? (${e instanceof Error ? e.message : String(e)})`;
        setLoad({ kind: "blocked", why });
      });
    return () => { alive = false; };
  }, []);

  const pick = (p: { email: string; name?: string }, demo = true) => {
    try { localStorage.setItem(MOCK_DEMO_KEY, demo ? "1" : "0"); } catch { /* ignore */ }
    auth.signInDev(p.email, p.name);
  };

  function submitOther(e: React.FormEvent) {
    e.preventDefault();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) { setErr("Type an email like you@example.com."); return; }
    pick({ email }, !fresh);
  }

  return (
    <main className="signin">
      <div className="signin-card">
        <CleaverMark className="signin-mark" title="CleanCleaver" />
        <div className="stack-sm">
          <p className="kicker">CleanCleaver</p>
          <h1>Food safety that runs itself.</h1>
          <p className="muted">Daily checks, cleaning guides and food dates for your kitchen, business or home.</p>
        </div>

        {/* The demo (owner, 2026-10-12, demo option (a)): one question, two answers; each opens its 8-screen tour (lib/demoTour.ts). */}
        {config.demo && (
          <section className="stack-sm" aria-labelledby="tour-h" data-testid="demo-pick">
            <h2 id="tour-h" style={{ fontSize: 22 }}>{TOUR_WORDS.pickTitle}</h2>
            <Button big block icon="chevron" onClick={() => { startTour("owner"); pick(TOUR_PERSON.owner); }} data-testid="demo-pick-owner">{TOUR_WORDS.pickOwner}</Button>
            <Button big block variant="secondary" icon="chevron" onClick={() => { startTour("employee"); pick(TOUR_PERSON.employee); }} data-testid="demo-pick-employee">{TOUR_WORDS.pickEmployee}</Button>
            <p className="caption">{TOUR_WORDS.pickNote}</p>
          </section>
        )}

        {!config.demo && <section className="stack-sm" aria-labelledby="who-h">
          <h2 id="who-h" style={{ fontSize: 22 }}>Pick who you are</h2>
          {load.kind === "loading" && <Skeleton rows={2} label="Loading people" />}
          {load.kind === "blocked" && <Blocked title="Sign-in isn't available">{load.why}</Blocked>}
          {load.kind === "ok" && (
            <ul className="who-list">
              {load.people.map((p) => (
                <li key={p.email}>
                  <button type="button" className="who" onClick={() => pick(p)}>
                    <span className="avatar" aria-hidden="true">{p.name.slice(0, 1)}</span>
                    <span className="grow"><strong style={{ display: "block" }}>{p.name}</strong><span className="caption">{[ROLE_LABEL[p.role], p.title !== ROLE_LABEL[p.role] ? p.title : null, p.orgName].filter(Boolean).join(" · ")}</span></span>
                    <Icon name="chevron" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>}

        {!config.demo && (
          other ? (
            <form onSubmit={submitOther} className="stack-sm" noValidate>
              <label htmlFor="dev-email" className="field-label">Someone else</label>
              <div className="inline-add">
                <input id="dev-email" type="email" autoComplete="email" value={email} onChange={(e) => { setEmail(e.target.value); setErr(undefined); }} placeholder="you@example.com" />
                <Button type="submit" icon="chevron" iconEnd>Sign in</Button>
              </div>
              {config.apiMode === "mock" && <label className="check small"><input type="checkbox" checked={fresh} onChange={(e) => setFresh(e.target.checked)} /> Start with an empty business (to try first-time setup)</label>}
              {err && <p className="field-msg" role="alert">{err}</p>}
            </form>
          ) : <div><button type="button" className="linklike" onClick={() => setOther(true)}>Someone else? Type an email</button></div>
        )}

        {/* The sample has no accounts: an email code here would only make a pretend one that vanishes with the browser. */}
        {config.demo
          ? <div><a className="linklike" href={freeTrialUrl()} data-testid="demo-pick-free">{TOUR_WORDS.free}</a></div>
          : <div><button type="button" className="linklike" onClick={onEmail}>Try email sign-in</button></div>}

        <p className="caption"><Icon name="prove" size={14} /> Demo sign-in: no passwords. Health answers are never shown to anyone, only whether someone is OK to work.</p>
      </div>
    </main>
  );
}

/* ─────────────── real sign-in: Google or a 6-digit email code (docs/AUTH.md) ─────────────── */

const RESEND_AFTER_S = 30;
type Step = { at: "start" } | { at: "email" } | { at: "code"; email: string; sentTo: string; demoCode?: string; sentAt: number };

/**
 * One question per screen (docs/EASY.md): how to sign in → your email → the 6 digits. The code signs in by itself
 * once all six are in. Errors are the contract's plain words.
 */
export function EmailSignIn({ onBack }: { onBack?: () => void }) {
  const auth = useAuth();
  // An email typed on cleancleaver.com/get/ (lib/appLead.ts): sign-up opens on the email step with it already typed.
  const [typed] = useState(() => storedAppEmail());
  const here = useLocation();
  const [step, setStep] = useState<Step>(() => (typed && new URLSearchParams(here.search).get("signup") === "1" ? { at: "email" } : { at: "start" }));
  const [email, setEmail] = useState(typed ?? "");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState<"google" | "send" | "verify">();
  const [err, setErr] = useState<string>();
  const [now, setNow] = useState(() => Date.now());
  const tried = useRef<string>("");
  const location = useLocation();
  const [ref] = useState(() => storedRef());
  const [buy] = useState(() => pendingBuy());
  const [kept] = useState(() => carryLine(demoCarry()));
  const [read] = useState(() => { const r = pendingRead(); return r ? readWords(r) : null; });
  const starting = new URLSearchParams(location.search).get("signup") === "1" || !!ref || !!buy;
  const [serverGoogle, setServerGoogle] = useState(false);
  const [referrer, setReferrer] = useState<string>();
  // Hidden until the server says Google works: a button that fails is worse than none.
  const google = googleConfigured() && serverGoogle;
  // Google is one tap: first for a returning person and for a buyer; a plain sign-up keeps the email code first (Wave 1 #1).
  const googleFirst = !starting || !!buy;

  useEffect(() => {
    let alive = true;
    auth.client.signinConfig?.().then((c) => { if (alive) setServerGoogle(c.google); }, () => { /* no answer: keep it hidden */ });
    return () => { alive = false; };
  }, [auth.client]);
  useEffect(() => {
    if (!ref) return;
    let alive = true;
    auth.client.referrerOf?.(ref.code).then((r) => { if (alive && r.firstName) setReferrer(r.firstName); }, () => { /* unknown code: no name */ });
    return () => { alive = false; };
  }, [auth.client, ref]);

  const signedIn = useCallback((g: SessionGrant) => {
    // The in-browser demo: a person signing in by email starts with their own empty account, not the sample shop.
    if (config.apiMode === "mock") { try { localStorage.setItem(MOCK_DEMO_KEY, "0"); } catch { /* ignore */ } }
    auth.signInWithSession(g);
  }, [auth]);

  async function withGoogle() {
    setBusy("google"); setErr(undefined);
    try { signedIn(await auth.client.google(await googleIdToken())); }
    catch (e) { if (!(e instanceof GoogleCancelled)) setErr(authMessage(e)); }
    finally { setBusy(undefined); }
  }

  async function send(to: string) {
    if (!isEmail(to)) { setErr(AUTH_MESSAGES.bad_email); return; }
    setBusy("send"); setErr(undefined);
    try {
      const r = await auth.client.emailStart(to);
      clearAppEmail(); // the code is on its way: the typed address has done its job
      setCode(""); tried.current = "";
      setNow(Date.now());
      setStep({ at: "code", email: to.trim().toLowerCase(), sentTo: r.sentTo, demoCode: r.demoCode, sentAt: Date.now() });
    } catch (e) { setErr(authMessage(e)); } finally { setBusy(undefined); }
  }

  const verify = useCallback(async (to: string, digits: string) => {
    tried.current = digits;
    setBusy("verify"); setErr(undefined);
    try { signedIn(await auth.client.emailVerify(to, digits)); }
    catch (e) { setErr(authMessage(e)); setCode(""); tried.current = ""; setBusy(undefined); }
  }, [auth.client, signedIn]);

  // Sign in by itself as soon as all six digits are in.
  const full = /^\d{6}$/.test(code);
  useEffect(() => {
    if (step.at === "code" && full && !busy && tried.current !== code) void verify(step.email, code);
  }, [step, full, code, busy, verify]);

  // "Send a new code" wakes up after 30 seconds.
  useEffect(() => {
    if (step.at !== "code") return;
    const sentAt = step.sentAt;
    const t = setInterval(() => { const n = Date.now(); setNow(n); if (n - sentAt >= RESEND_AFTER_S * 1000) clearInterval(t); }, 1000);
    return () => clearInterval(t);
  }, [step]);

  const shell = (children: ReactNode) => (
    <main className="signin">
      <div className="signin-card">
        <CleaverMark className="signin-mark" title="CleanCleaver" />
        {auth.notice && step.at === "start" && <Notice tone="warn" title={auth.notice} />}
        {children}
      </div>
    </main>
  );

  if (step.at === "email") {
    return shell(
      <form className="stack" noValidate onSubmit={(e) => { e.preventDefault(); void send(email); }}>
        <h1>What's your email?</h1>
        <p className="lede">We'll email you a 6-digit code. No password.</p>
        <Field label="Your email" error={err}>
          {(id) => <input id={id} type="email" inputMode="email" autoComplete="email" autoCapitalize="off" autoCorrect="off" spellCheck={false} autoFocus
            value={email} onChange={(e) => { setEmail(e.target.value); setErr(undefined); }} placeholder="you@example.com" />}
        </Field>
        <div className="form-actions">
          <Button big block type="submit" icon="chevron" iconEnd busy={busy === "send"}>Email me a code</Button>
          <Button variant="ghost" icon="back" onClick={() => { setErr(undefined); setStep({ at: "start" }); }}>Go back</Button>
        </div>
      </form>,
    );
  }

  if (step.at === "code") {
    const left = Math.max(0, RESEND_AFTER_S - Math.floor((now - step.sentAt) / 1000));
    return shell(
      <div className="stack">
        <h1>Type the code</h1>
        <p className="lede">We sent 6 digits to {step.sentTo}. The code works for 10 minutes.</p>
        {step.demoCode && <Notice tone="info" title={`Demo: your code is ${step.demoCode}`} />}
        <DigitBoxes value={code} onChange={(c) => { setCode(c); setErr(undefined); }} disabled={busy === "verify"} />
        {busy === "verify" && <p className="muted" role="status">Checking the code…</p>}
        {err && <p className="field-msg" role="alert"><Icon name="alert" size={16} />{err}</p>}
        <div className="form-actions">
          <Button variant="secondary" icon="undo" disabled={left > 0} busy={busy === "send"} reason={`You can send a new code in ${left} seconds.`}
            onClick={() => void send(step.email)}>Send a new code</Button>
          <Button variant="ghost" icon="back" onClick={() => { setErr(undefined); setCode(""); setStep({ at: "email" }); }}>Use a different email</Button>
        </div>
        {left > 0 && <p className="caption">No email? Check spam. You can send a new code in {left} seconds.</p>}
      </div>,
    );
  }

  return shell(
    <>
      <div className="stack-sm">
        <p className="kicker">CleanCleaver</p>
        {starting && referrer && <p className="lede"><strong>{referrer} invited you</strong></p>}
        {kept && <p className="lede" data-testid="signin-kept"><strong>{kept}</strong></p>}
        {starting && read && (
          <div className="stack-sm" data-testid="signin-read">
            <p className="small muted">{read.kicker}</p>
            <p className="lede"><strong>{read.title}</strong></p>
            <p className="small">{read.note}</p>
          </div>
        )}
        <h1>{starting ? `Start your ${TRIAL_DAYS} days free` : "Sign in"}</h1>
        {buy
          ? <p className="muted" data-testid="signin-buy">{pickedLine(buy.plan) ? `${pickedLine(buy.plan)} ` : ""}Sign in, answer two questions, then add a card on Stripe's page. No password.</p>
          : <p className="muted">Daily checks, cleaning guides and food dates for your kitchen, business or home.{starting ? " No password: we email you a code." : ""}</p>}
      </div>
      <div className="stack-sm">
        {google && googleFirst && <Button big block busy={busy === "google"} onClick={() => void withGoogle()} data-testid="signin-google">Continue with Google</Button>}
        <Button big block variant={google && googleFirst ? "secondary" : "primary"} icon="text" onClick={() => { setErr(undefined); setStep({ at: "email" }); }} data-testid="signin-email">Email me a code</Button>
        {google && !googleFirst && <Button big block variant="secondary" busy={busy === "google"} onClick={() => void withGoogle()} data-testid="signin-google">Continue with Google</Button>}
        {err && <p className="field-msg" role="alert"><Icon name="alert" size={16} />{err}</p>}
      </div>
      {onBack && <div><button type="button" className="linklike" onClick={onBack}>Go back to the demo sign-in</button></div>}
      <p className="caption"><Icon name="prove" size={14} /> No passwords. Health answers are never shown to anyone, only whether someone is OK to work.</p>
    </>,
  );
}
